// Pure reshaping of POST /api/projects/:id/generate-estimate into the rows the
// bulk-generate confirmation dialog renders. Kept out of the component so the
// grouping/sorting is machine-verifiable (web/test/estimateSummary.test.js).

import type { EstimateResponse, EstimateRouteRow } from "./types.js";

export type { EstimateResponse, EstimateRouteRow };

/** Below this projected total the dialog still appears but leads with the item
 *  count rather than the money (G-5 / sizing table). */
export const BULK_CONFIRM_THRESHOLD_USD = 1.0;

export interface EstimateSummary {
  /** Total deliverables in the previewed set (priced or not). */
  count: number;
  totalUsd: number;
  /** Deliverables with no price (unresolvable route, or a route with no rates). */
  unpriced: number;
  /** Per-route breakdown, biggest cost first. */
  rows: EstimateRouteRow[];
  /** `unresolved` collapsed to one entry per reason, most common first. */
  unresolvedByReason: Array<{ reason: string; count: number }>;
  /** True when the dialog should lead with the dollar figure rather than the count. */
  leadWithCost: boolean;
}

export function summarizeEstimate(res: EstimateResponse): EstimateSummary {
  const rows = [...res.byRoute].sort((a, b) => b.usd - a.usd || a.routeId.localeCompare(b.routeId));

  const reasonCounts = new Map<string, number>();
  for (const u of res.unresolved ?? []) {
    reasonCounts.set(u.reason, (reasonCounts.get(u.reason) ?? 0) + 1);
  }
  const unresolvedByReason = [...reasonCounts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));

  return {
    count: res.count,
    totalUsd: res.totalUsd,
    unpriced: res.unpriced,
    rows,
    unresolvedByReason,
    leadWithCost: res.totalUsd >= BULK_CONFIRM_THRESHOLD_USD,
  };
}
